/* eslint-disable @typescript-eslint/no-explicit-any */
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import {
  getIncidentSeverityColor,
  getIncidentStatusColor,
} from '../../commons/rps-incidence-helpers';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import {
  CreateMaintenanceOrderDto,
  IncidenceDetailsDto,
  MaintenanceOrderTableDto,
} from 'apps/marsinet/src/app/typescript-axios';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import EventList from '../../../sessions/components/event-list/event-list';
import { CloseIncidenceDisclaimerDialog } from './closeIncidenceDisclaimerDialog';
import ModalAssignMaintenanceOrder from '../assign-maintenance-order/assign-maintenance-order-modal';
import ModalCreateMaintenanceOrder from '../maintenance-orders/create-maintenance-order-modal';
import {
  formatUtcTimestamp,
  localDateTimeToUtc,
} from '../../../../../utils/timezone-utils';
import ModalSuggestDeviceState from 'apps/marsinet/src/app/components/modal-suggest-device-state/modal-suggest-device-state';
import { useSuggestDeviceState } from 'apps/marsinet/src/app/components/modal-suggest-device-state/use-suggest-device-state';

// ───────────────────────────────────────────────────────────────────────────
// Helpers

const formatDate = (
  value: string | Date | null | undefined,
  timeZone: string,
  locale = 'es-ES',
  withTime = false
): string | undefined => {
  if (!value) return undefined;
  // Los valores son instantes UTC; solo su presentación depende de la zona seleccionada.
  return formatUtcTimestamp(value, timeZone, locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

/**
 * Tiempo de respuesta: desde que se registra la incidencia hasta la ejecución
 * de la primera OM. Devuelve `undefined` si falta alguno de los dos extremos,
 * es decir, mientras no haya ninguna orden con fecha de ejecución.
 */
const getResponseTimeMs = (
  incidence: IncidenceDetailsDto | null,
  orders: MaintenanceOrderTableDto[]
): number | undefined => {
  if (!incidence?.start_date) return undefined;

  const start = new Date(incidence.start_date).getTime();
  if (Number.isNaN(start)) return undefined;

  const executionTimes = orders
    .map((order) =>
      order.execution_date ? new Date(order.execution_date).getTime() : NaN
    )
    .filter((time) => !Number.isNaN(time));

  if (executionTimes.length === 0) return undefined;

  return Math.min(...executionTimes) - start;
};

/** Formatea una duración en días / horas / minutos, sin unidades vacías. */
const formatDuration = (milliseconds?: number): string | undefined => {
  if (milliseconds === undefined) return undefined;

  // Una ejecución anterior al alta de la incidencia es un dato incoherente,
  // pero se muestra tal cual en vez de ocultarlo.
  const sign = milliseconds < 0 ? '-' : '';
  const totalMinutes = Math.floor(Math.abs(milliseconds) / 60_000);

  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} d`);
  if (hours > 0) parts.push(`${hours} h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} min`);

  return `${sign}${parts.join(' ')}`;
};

/**
 * Espejo de FINISHED_MAINTENANCE_ORDER_STATUSES del backend. Aquí sólo
 * llegan las etiquetas legibles del estado, no los IDs, así que la
 * comparación se hace sobre el texto normalizado.
 */
const isOrderFinished = (order: MaintenanceOrderTableDto): boolean =>
  order.status == 'Terminado';

/**
 * Misma regla que aplica el backend al cerrar: la incidencia debe seguir
 * abierta y no puede quedar ninguna orden de mantenimiento sin finalizar.
 */
const canCloseIncidence = (
  incidence: IncidenceDetailsDto | null,
  orders: MaintenanceOrderTableDto[]
): boolean => {
  if (!incidence) return false;
  if (incidence.close_date) return false;

  return orders.every(isOrderFinished);
};

// ───────────────────────────────────────────────────────────────────────────
// Subcomponentes

const DetailItem = ({
  label,
  value,
  className = 'col-12 md:col-6 lg:col-4',
}: {
  label: string;
  value?: ReactNode;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={`${className} mb-3`}>
      <div className="font-bold text-900 mb-1">{label}</div>
      <div className="text-700 line-height-3">
        {value || t('pages.incidents.notAvailable')}
      </div>
    </div>
  );
};

const AlarmsSection = ({ deviceMarsinetId }: { deviceMarsinetId?: string }) => {
  const { t } = useTranslation();
  const { setError } = useContext(MarsinetContext);

  const [alarms, setAlarms] = useState<any[]>([]);
  const [selected, setSelected] = useState('1');
  const [updateAlarms, setUpdateAlarms] = useState<boolean>(false);

  useEffect(() => {
    if (!deviceMarsinetId) {
      setAlarms([]);
      return;
    }

    let cancelled = false;

    const loadAlarms = async () => {
      try {
        const response =
          await apiServices.alarmApi.alarmControllerFindAllAlarmOfDevice(
            deviceMarsinetId,
            parseInt(selected, 10)
          );

        if (!cancelled) setAlarms(response?.data || []);
      } catch (error: any) {
        console.error('[AlarmsSection] Error cargando alarmas', error);
        if (cancelled) return;

        setAlarms([]);

        if (error?.response?.status !== 404) {
          setError(true);
        }
      }
    };

    loadAlarms();

    return () => {
      cancelled = true;
    };
  }, [deviceMarsinetId, selected, updateAlarms]);

  return (
    <div className="mt-4 pt-4 border-top-1 surface-border">
      <div className="flex align-items-center justify-content-center gap-2 mb-3 w-full">
        <i className="pi pi-exclamation-triangle text-xl text-900" />
        <span className="text-lg font-bold text-900 text-center">
          {t('pages.incidents.alarms.title')}
        </span>
      </div>

      {!deviceMarsinetId ? (
        <div className="col-12 text-center py-4 text-600">
          {t('pages.alarms.noAlarms')}
        </div>
      ) : (
        <>
          <div className="flex justify-content-end align-items-center w-full mt-3 mb-4">
            <div className="flex border-1 border-round-md overflow-hidden border-primary">
              {[
                { label: '1S', value: '1' },
                { label: '1M', value: '4' },
                { label: '3M', value: '13' },
              ].map((range) => (
                <Button
                  key={range.value}
                  label={range.label}
                  className={`border-none border-noround px-3 py-2 font-bold ${selected === range.value
                    ? 'bg-primary text-white'
                    : 'bg-blue-50 text-primary'
                    }`}
                  onClick={() => setSelected(range.value)}
                />
              ))}
            </div>
          </div>

          <EventList
            alarms={alarms}
            incidence={true}
            setUpdateAlarms={setUpdateAlarms}
          />
        </>
      )}
    </div>
  );
};

/** Cinco filas caben sin empujar el resto de la ficha fuera de pantalla. */
const ORDERS_PER_PAGE = 5;

const MaintenanceOrdersSection = ({
  orders,
}: {
  orders: MaintenanceOrderTableDto[];
}) => {
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  const navigate = useNavigate();


  // Para hacer clicable directamente la fila
  const goToMaintenanceOrder = (order: MaintenanceOrderTableDto) => {
    const maintenanceOrderId = order.id;

    if (!maintenanceOrderId) {
      return;
    }

    navigate(
      `/authenticated/maintenance-order/${encodeURIComponent(maintenanceOrderId)}`
    );
  };

  const textBody = (value?: string | null) =>
    value || (
      <span className="text-400 italic">
        {t('pages.incidents.notAvailable')}
      </span>
    );

  const statusBody = (order: MaintenanceOrderTableDto) =>
    order.status ? (
      <Tag
        value={order.status}
        style={{
          backgroundColor: getIncidentStatusColor(order.status),
          color: '#fff',
        }}
      />
    ) : (
      textBody(null)
    );

  //Helpers para estilos de tabla
  const emptyValue = (
    <span className="text-400 italic">{t('pages.incidents.notAvailable')}</span>
  );

  /** Una sola línea, con "…" si no cabe. El valor completo se ve al pasar el ratón. */
  const oneLineBody = (value?: string | null) =>
    value ? (
      <div
        className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
        title={value}
      >
        {value}
      </div>
    ) : (
      emptyValue
    );

  /** Máximo 2 líneas: así una descripción larga no estira la fila. */
  const clampedBody = (value?: string | null) =>
    value ? (
      <div
        title={value}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {value}
      </div>
    ) : (
      emptyValue
    );

  /** Fecha arriba y hora abajo, como en el listado de incidencias. Ocupa menos ancho. */
  const dateBody = (value?: string | Date | null) => {
    if (!value) return emptyValue;

    return (
      <div className="white-space-nowrap">
        <span className="block">
          {formatUtcTimestamp(value, selectedTimezone, i18n.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </span>
        <span className="text-sm text-600">
          {formatUtcTimestamp(value, selectedTimezone, i18n.language, {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    );
  };
  return (
    <div className="mt-4 pt-4 border-top-1 surface-border">
      <div className="flex align-items-center justify-content-center gap-2 mb-3 w-full">
        <i className="pi pi-wrench text-xl text-900" />
        <span className="text-lg font-bold text-900 text-center">
          {t('pages.incidents.omAssigned')}
        </span>
      </div>

      {orders.length === 0 ? (
        <div className="surface-100 border-round-lg p-3 text-700">
          {t('pages.incidents.notAssigned')}
        </div>
      ) : (
        <DataTable
          value={orders}
          dataKey="id"
          paginator={orders.length > ORDERS_PER_PAGE}
          rows={ORDERS_PER_PAGE}
          emptyMessage={t('pages.incidents.notAssigned')}
          tableStyle={{ tableLayout: 'fixed' }}
          className="mb-3"
          selectionMode="single"
          onSelectionChange={(e) =>
            goToMaintenanceOrder(e.value as MaintenanceOrderTableDto | null)
          }
          rowHover
        >
          <Column
            field="code"
            header={t('pages.maintenance_orders.code')}
            style={{ width: '9%' }}
            body={(order: MaintenanceOrderTableDto) => textBody(order.code)}
          />

          <Column
            field="description"
            header={t('pages.incidents.description')}
            style={{ width: '20%' }}
            body={(o: MaintenanceOrderTableDto) => clampedBody(o.description)}
          />

          <Column
            field="status"
            header={t('pages.incidents.status')}
            style={{ width: '9%' }}

            body={statusBody}
          />

          <Column
            field="priority"
            header={t('pages.incidents.priority')}
            style={{ width: '8%' }}
            body={(o: MaintenanceOrderTableDto) =>
              oneLineBody(o.priority == null ? null : String(o.priority))
            }
          />
          <Column
            field="creation_date"
            header={t('pages.incidents.created_at')}
            style={{ width: '10%' }}
            body={(order: MaintenanceOrderTableDto) =>
              dateBody(order.creation_date)
            }
          />

          <Column
            field="execution_date"
            header={t('pages.maintenance_orders.execution_date')}
            style={{ width: '10%' }}
            body={(order: MaintenanceOrderTableDto) =>
              dateBody(order.execution_date)
            }
          />

          <Column
            field="closing_date"
            header={t('pages.incidents.date_closed')}
            style={{ width: '10%' }}
            body={(order: MaintenanceOrderTableDto) =>
              dateBody(order.closing_date)
            }
          />

          <Column
            field="responsible"
            header={t('pages.incidents.assigned')}
            style={{ width: '13%' }}
            body={(o: MaintenanceOrderTableDto) => oneLineBody(o.responsible)}
          />


        </DataTable>
      )}
    </div>
  );
};

export function DetailsIncident() {
  const { t, i18n } = useTranslation();
  const { id_incident } = useParams();
  const { setError, selectedTimezone } = useContext(MarsinetContext);
  const { showToast: show } = useToast();
  const navigate = useNavigate();
  const { suggestForDevice, modalProps } = useSuggestDeviceState('in_maintenance');
  const { suggestForDevice: suggestAfterClose, modalProps: closeModalProps } =
    useSuggestDeviceState(['operations', 'on_client']);


  const [incidence, setIncidence] = useState<IncidenceDetailsDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);

  const [maintenanceOrders, setMaintenanceOrders] = useState<
    MaintenanceOrderTableDto[]
  >([]);

  const [closeDialogVisible, setCloseDialogVisible] = useState(false);
  const [closingIncidence, setClosingIncidence] = useState(false);
  const [assignOrderModalVisible, setAssignOrderModalVisible] = useState(false);
  const [createOrderModalVisible, setCreateOrderModalVisible] = useState(false);
  // Se incrementa tras un cierre para recargar el detalle y las órdenes.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id_incident) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    const loadIncidentDetail = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        const response =
          await apiServices.incidenceApi.incidenceControllerGetIncidenceDetailsById(
            id_incident
          );

        const maintenanceOrdersResponse =
          await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetMaintenanceOrdersByIncidence(
            response.data.id
          );

        setIncidence(response?.data ?? null);
        setMaintenanceOrders(maintenanceOrdersResponse?.data ?? []);
      } catch (error: any) {
        console.error('[DetailsIncident] Error cargando el detalle', error);

        if (error?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadIncidentDetail();
  }, [id_incident, reloadKey]);

  const reloadDetail = () => setReloadKey((key) => key + 1);

  /**
   * Crear una OM desde la incidencia debe dejarla asociada: el endpoint de
   * creación no conoce la incidencia, así que se enlaza en un segundo paso.
   */
  const createAndLinkMaintenanceOrder = async (
    values: CreateMaintenanceOrderDto, deviceId?: string
  ) => {
    const response =
      await apiServices.maintenanceOrderApi.maintenanceOrdersControllerCreateMaintenanceOrder(
        {
          ...values,
          serial_number: incidence.device_serial || values.serial_number,
          origin_incidence_id: id_incident,
        }
      );

    const created = response.data;

    try {
      if (created?.id && id_incident) {
        // Body primero, id después, igual que el resto del cliente generado.
        await apiServices.incidenceApi.incidenceControllerAssignMaintenanceOrders(
          [created.id],
          id_incident
        );
      }

      show(
        'success',
        t('messages.success'),
        t('messages.maintenance_orders.create')
      );
    } catch (error: any) {
      // La OM existe pero quedó suelta: hay que decirlo, no fallar en silencio.
      console.error('[DetailsIncident] Error asociando la OM creada', error);

      show(
        'warn',
        t('messages.warning'),
        error?.response?.data?.message ||
        t('messages.maintenance_orders.assign_error')
      );
    } finally {
      reloadDetail();
      suggestForDevice(deviceId ?? incidence.device_marsinet_id ?? undefined);
    }

    return created;
  };

  const handleConfirmClose = async (closeData: {
    selectedDate: Date | null;
    comment: string;
  }) => {
    if (!id_incident) return;

    try {
      setClosingIncidence(true);

      // Ojo: el cliente generado recibe el body primero y el id después.
      // Envía la hora seleccionada como instante UTC para que la API no dependa de la zona.
      await apiServices.incidenceApi.incidenceControllerCloseIncidence(
        {
          closing_datetime: localDateTimeToUtc(
            closeData.selectedDate ?? new Date(),
            selectedTimezone
          ),
          comment: closeData.comment,
        },
        id_incident
      );

      setCloseDialogVisible(false);
      reloadDetail();

      show('success', t('messages.success'), t('messages.incidence.close'));
      suggestAfterClose(incidence?.device_marsinet_id ?? undefined);
    } catch (error: any) {
      console.error('[DetailsIncident] Error cerrando la incidencia', error);

      if (error instanceof RangeError) {
        show('error', t('messages.error'), error.message);
        return;
      }

      setCloseDialogVisible(false);

      // 409: el backend detectó órdenes sin finalizar que el front no veía
      // (p. ej. otro usuario reabrió una orden mientras tanto), así que
      // recargamos para que el botón desaparezca.
      if (error?.response?.status === 409) {
        reloadDetail();
      }

      show(
        'error',
        t('messages.error'),
        error?.response?.data?.message || t('messages.incidence.close_error')
      );
    } finally {
      setClosingIncidence(false);
    }
  };



  // ── Estados de carga / error ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="col-12 flex justify-content-center py-8">
        <ProgressSpinner style={{ width: '3rem', height: '3rem' }} />
      </div>
    );
  }

  if (notFound || !incidence) {
    return (
      <div className="col-12 xl:pl-7 xl:pr-7">
        <div className="surface-card p-5 shadow-3 border-round-xl text-center">
          <i className="pi pi-exclamation-circle text-4xl text-500 mb-3 block" />
          <p className="text-700 mb-4">{t('pages.incidents.notDetails')}</p>
          <Button
            label={t('goBack')}
            icon="pi pi-arrow-left"
            onClick={() => navigate('/authenticated/incidents')}
          />
        </div>
      </div>
    );
  }

  const isOpen = !incidence.close_date;
  const statusLabel = isOpen
    ? t('pages.incidents.open')
    : t('pages.incidents.closed');

  // TODO: añadir aquí la comprobación de permiso (incidents.close) si existe
  // un hook equivalente al PermissionGuard del backend.
  const showCloseButton = canCloseIncidence(incidence, maintenanceOrders);


  // ── Vista ────────────────────────────────────────────────────────────────

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      {/* Cabecera */}
      <div className="col-12">
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div className="p-3 flex align-items-center flex-wrap">
            <Button
              icon="pi pi-angle-left"
              rounded
              onClick={() => navigate('/authenticated/incidents')}
              severity="secondary"
              className="mr-2 mb-2 sm:mr-4"
              aria-label="Back"
            />

            <div className="mr-3">
              <i className="pi pi-file text-2xl md:text-3xl text-900" />
            </div>
            <div className="font-medium text-lg sm:text-xl md:text-2xl text-700">
              {t('pages.incidents.codeIncidence')} {incidence.code}
            </div>
          </div>

          <div className="p-3 flex align-items-center flex-wrap gap-2">
            {isOpen && (
              <>
                <Button
                  label={t('pages.incidents.buttonAssing')}
                  icon="pi pi-link"
                  severity="secondary"
                  outlined
                  onClick={() => setAssignOrderModalVisible(true)}
                />

                <Button
                  label={t('pages.incidents.buttonCreateOM')}
                  icon="pi pi-plus"
                  severity="secondary"
                  outlined
                  onClick={() => setCreateOrderModalVisible(true)}
                />
              </>
            )}

            {showCloseButton && (
              <Button
                label={t('pages.incidents.buttonCloseIncidence')}
                icon="pi pi-check-circle"
                severity="danger"
                onClick={() => setCloseDialogVisible(true)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="col-12 grid mx-0">
        <div className="surface-card border-round-xl shadow-3 px-3 col-12 mb-5">
          {/* Datos de la incidencia */}
          <div className="p-4">
            <div className="flex align-items-center justify-content-center gap-2 w-full mb-4">
              <i className="pi pi-info-circle text-xl text-900" />
              <span className="text-lg font-bold text-900 text-center">
                {t('pages.incidents.details')}
              </span>
            </div>

            <div className="grid">
              <DetailItem
                label={t('pages.incidents.description')}
                value={incidence.description}
                className="col-12"
              />

              <DetailItem
                label={t('pages.incidents.status')}
                value={
                  incidence.status ? (
                    <Tag
                      value={incidence.status}
                      style={{
                        backgroundColor: getIncidentStatusColor(
                          incidence.status
                        ),
                        color: '#fff',
                      }}
                    />
                  ) : (
                    t('pages.incidents.notAvailable')
                  )
                }
              />

              <DetailItem
                label={t('pages.incidents.impact')}
                value={
                  incidence.severity ? (
                    <Tag
                      value={incidence.severity}
                      style={{
                        backgroundColor: getIncidentSeverityColor(
                          incidence.severity
                        ),
                        color: '#fff',
                      }}
                    />
                  ) : undefined
                }
              />

              <DetailItem
                label={t('pages.incidents.type')}
                value={(incidence as any).type}
              />

              <DetailItem
                label={t('pages.incidents.priority')}
                value={incidence.priority}
              />

              <DetailItem
                label={t('pages.incidents.serial_number')}
                value={incidence.device_serial}
              />

              <DetailItem
                label={t('pages.incidents.start_time')}
                value={formatDate(
                  incidence.start_date,
                  selectedTimezone,
                  i18n.language,
                  true
                )}
              />

              <DetailItem
                label={t('pages.incidents.date_closed')}
                value={formatDate(
                  incidence.close_date,
                  selectedTimezone,
                  i18n.language,
                  true
                )}
              />

              <DetailItem
                label={t('pages.incidents.response_time')}
                value={formatDuration(
                  getResponseTimeMs(incidence, maintenanceOrders)
                )}
              />

              <DetailItem
                label={t('pages.incidents.origin')}
                value={incidence.origin}
              />

              <DetailItem
                label={t('pages.incidents.detection')}
                value={incidence.report_channel}
              />

              <DetailItem
                label={t('pages.incidents.detected_by')}
                value={incidence.detected_by}
              />
            </div>

            <MaintenanceOrdersSection orders={maintenanceOrders} />

            <AlarmsSection
              deviceMarsinetId={incidence.device_marsinet_id ?? undefined}
            />
          </div>
          <div className="col-12 mb-4" />
        </div>
      </div>

      <ModalAssignMaintenanceOrder
        visible={assignOrderModalVisible}
        setVisible={setAssignOrderModalVisible}
        incidentId={incidence.id}
        deviceSerial={incidence.device_serial}
        onAssignedSuccess={reloadDetail}
      />

      <ModalCreateMaintenanceOrder
        visible={createOrderModalVisible}
        setVisible={setCreateOrderModalVisible}
        maintenanceOrderFunction={createAndLinkMaintenanceOrder}
        initialDeviceSerial={incidence.device_serial}
        lockDevice
        maintenanceTypeRestriction="corrective"
      />

      <ModalSuggestDeviceState {...modalProps} />
      <ModalSuggestDeviceState {...closeModalProps} />

      <CloseIncidenceDisclaimerDialog
        visible={closeDialogVisible}
        loading={closingIncidence}
        incidentCode={incidence.code || incidence.id}
        onCancel={() => setCloseDialogVisible(false)}
        onConfirm={handleConfirmClose}
      />
    </div>
  );
}
